import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { DataTable } from '../components/DataTable';
import {
  History,
  Search,
  RefreshCw,
  Filter,
  User,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye,
  Database,
  Activity,
  Shield,
  XCircle,
} from 'lucide-react';

const getActionIcon = (accion) => {
  const lower = accion?.toLowerCase() || '';
  if (lower.includes('crear') || lower.includes('create') || lower.includes('cerrado'))
    return <CheckCircle size={16} className="text-green-600" />;
  if (lower.includes('eliminar') || lower.includes('delete') || lower.includes('rechaz'))
    return <XCircle size={16} className="text-red-600" />;
  if (lower.includes('editar') || lower.includes('update') || lower.includes('modific'))
    return <Activity size={16} className="text-blue-600" />;
  if (lower.includes('ejecutar') || lower.includes('execute'))
    return <Activity size={16} className="text-purple-600" />;
  if (lower.includes('reabiert'))
    return <RefreshCw size={16} className="text-amber-600" />;
  return <History size={16} className="text-slate-500" />;
};

const getActionBadgeColor = (accion) => {
  const lower = accion?.toLowerCase() || '';
  if (lower.includes('crear') || lower.includes('create') || lower.includes('cerrado'))
    return 'bg-green-100 text-green-700';
  if (lower.includes('eliminar') || lower.includes('delete') || lower.includes('rechaz'))
    return 'bg-red-100 text-red-700';
  if (lower.includes('editar') || lower.includes('update') || lower.includes('modific'))
    return 'bg-blue-100 text-blue-700';
  if (lower.includes('ejecutar') || lower.includes('execute'))
    return 'bg-purple-100 text-purple-700';
  if (lower.includes('reabiert'))
    return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-700';
};

const getEntityIcon = (entidad) => {
  switch (entidad?.toLowerCase()) {
    case 'monitor': return <Shield size={16} className="text-purple-600" />;
    case 'alert': return <AlertTriangle size={16} className="text-amber-600" />;
    case 'caso': return <FileText size={16} className="text-blue-600" />;
    case 'cierre': return <Database size={16} className="text-teal-600" />;
    case 'f8125': return <FileText size={16} className="text-indigo-600" />;
    default: return <History size={16} className="text-slate-500" />;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
};

const formatPeriodo = (periodo) => {
  if (!periodo || periodo.length !== 6) return periodo || '-';
  const meses = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return `${meses[parseInt(periodo.substring(4, 6))]} ${periodo.substring(0, 4)}`;
};

// ========== GENERAL COLUMNS ==========
const generalColumns = [
  {
    key: 'icono',
    label: '',
    width: 40,
    alwaysVisible: true,
    resizable: false,
    render: (row) => getActionIcon(row.accion),
  },
  {
    key: 'timestamp',
    label: 'Fecha/Hora',
    sortable: true,
    filterable: true,
    filterType: 'date',
    render: (row) => <span className="text-sm">{formatDate(row.timestamp)}</span>,
  },
  {
    key: 'usuario_nombre',
    label: 'Usuario',
    sortable: true,
    filterable: true,
    render: (row) => (
      <div className="flex items-center gap-2">
        <User size={14} className="text-slate-400" />
        <span className="text-sm">{row.usuario_nombre || row.usuario_id || '-'}</span>
      </div>
    ),
  },
  {
    key: 'accion',
    label: 'Acción',
    sortable: true,
    filterable: true,
    filterType: 'select',
    filterOptions: [
      { value: 'crear', label: 'Crear' },
      { value: 'editar', label: 'Editar' },
      { value: 'eliminar', label: 'Eliminar' },
      { value: 'ejecutar', label: 'Ejecutar' },
    ],
    render: (row) => (
      <Badge className={getActionBadgeColor(row.accion)}>{row.accion}</Badge>
    ),
  },
  {
    key: 'entidad',
    label: 'Entidad',
    sortable: true,
    filterable: true,
    filterType: 'select',
    filterOptions: [
      { value: 'monitor', label: 'Monitor' },
      { value: 'alert', label: 'Alerta' },
      { value: 'caso', label: 'Caso' },
      { value: 'usuario', label: 'Usuario' },
    ],
    render: (row) => (
      <div className="flex items-center gap-2">
        {getEntityIcon(row.entidad)}
        <span className="text-sm capitalize">{row.entidad || '-'}</span>
      </div>
    ),
  },
  {
    key: 'entidad_id',
    label: 'ID Entidad',
    render: (row) => (
      <span className="text-xs font-mono text-slate-500">
        {row.entidad_id ? `${row.entidad_id.substring(0, 12)}...` : '-'}
      </span>
    ),
  },
];

// ========== CIERRE COLUMNS ==========
const cierreColumns = [
  {
    key: 'icono',
    label: '',
    width: 40,
    alwaysVisible: true,
    resizable: false,
    render: (row) => getActionIcon(row.accion),
  },
  {
    key: 'fecha',
    label: 'Fecha/Hora',
    sortable: true,
    render: (row) => <span className="text-sm">{formatDate(row.fecha)}</span>,
  },
  {
    key: 'usuario',
    label: 'Usuario',
    sortable: true,
    filterable: true,
    render: (row) => (
      <div className="flex items-center gap-2">
        <User size={14} className="text-slate-400" />
        <span className="text-sm">{row.usuario || '-'}</span>
      </div>
    ),
  },
  {
    key: 'accion',
    label: 'Acción',
    sortable: true,
    render: (row) => (
      <Badge className={getActionBadgeColor(row.accion)}>{row.accion}</Badge>
    ),
  },
  {
    key: 'origen',
    label: 'Tipo',
    sortable: true,
    filterable: true,
    filterType: 'select',
    filterOptions: [
      { value: 'cierre', label: 'Cierre' },
      { value: 'f8125', label: 'F8125' },
    ],
    render: (row) => (
      <Badge variant="outline" className={
        row.origen === 'cierre' ? 'border-teal-300 text-teal-700' : 'border-indigo-300 text-indigo-700'
      }>
        {row.origen === 'cierre' ? 'Cierre' : 'F8125'}
        {row.tipo ? ` - ${row.tipo}` : ''}
      </Badge>
    ),
  },
  {
    key: 'periodo',
    label: 'Período',
    sortable: true,
    render: (row) => <span className="font-medium">{formatPeriodo(row.periodo)}</span>,
  },
  {
    key: 'comentarios',
    label: 'Comentarios',
    render: (row) => (
      <span className="text-sm text-slate-500 truncate block max-w-[200px]">
        {row.comentarios || '-'}
      </span>
    ),
  },
];

const AuditLogs = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [generalLogs, setGeneralLogs] = useState([]);
  const [cierreLogs, setCierreLogs] = useState([]);

  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [columnFilters, setColumnFilters] = useState({});

  // Filtros de búsqueda (backend)
  const [filters, setFilters] = useState({
    usuario: '', entidad: '', accion: '', periodo: '',
  });

  // Detalle
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  useEffect(() => {
    loadLogs();
  }, [activeTab]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      if (activeTab === 'general') {
        const params = new URLSearchParams();
        if (filters.usuario) params.append('usuario_id', filters.usuario);
        if (filters.entidad) params.append('entidad', filters.entidad);
        if (filters.accion) params.append('accion', filters.accion);
        params.append('limit', '200');
        const res = await api.get(`/audit?${params.toString()}`);
        setGeneralLogs(res.data);
      } else {
        const params = new URLSearchParams();
        if (filters.periodo) params.append('periodo', filters.periodo);
        params.append('limit', '200');
        const res = await api.get(`/cierre-periodo/auditoria?${params.toString()}`);
        setCierreLogs(res.data);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Error al cargar logs de auditoría');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handleColumnFilterChange = useCallback((key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = () => {
    setFilters({ usuario: '', entidad: '', accion: '', periodo: '' });
  };

  // Client-side sorting + filtering
  const processData = useCallback((data) => {
    let filtered = [...data];

    // Apply column filters
    Object.entries(columnFilters).forEach(([key, value]) => {
      if (!value || value === '' || value === 'all') return;
      if (typeof value === 'object' && (value.from || value.to)) {
        // Date range filter
        filtered = filtered.filter((row) => {
          const dateField = key === 'timestamp' ? row.timestamp : row.fecha;
          if (!dateField) return false;
          const rowDate = new Date(dateField).toISOString().split('T')[0];
          if (value.from && rowDate < value.from) return false;
          if (value.to && rowDate > value.to) return false;
          return true;
        });
      } else {
        const q = String(value).toLowerCase();
        filtered = filtered.filter((row) => {
          const cellValue = row[key] || '';
          return String(cellValue).toLowerCase().includes(q);
        });
      }
    });

    // Sort
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        const cmp = String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }

    return filtered;
  }, [columnFilters, sortConfig]);

  const processedGeneral = processData(generalLogs);
  const processedCierre = processData(cierreLogs);

  // Add acciones column dynamically (with view button)
  const generalColumnsWithActions = [
    ...generalColumns,
    {
      key: 'acciones',
      label: '',
      width: 50,
      alwaysVisible: true,
      resizable: false,
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          data-testid={`view-audit-log-${row.id}`}
          onClick={(e) => { e.stopPropagation(); setSelectedLog(row); setShowDetailDialog(true); }}
        >
          <Eye size={14} />
        </Button>
      ),
    },
  ];

  const cierreColumnsWithActions = [
    ...cierreColumns,
    {
      key: 'acciones',
      label: '',
      width: 50,
      alwaysVisible: true,
      resizable: false,
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          data-testid={`view-cierre-log-${row.id}`}
          onClick={(e) => { e.stopPropagation(); setSelectedLog(row); setShowDetailDialog(true); }}
        >
          <Eye size={14} />
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 rounded-lg">
            <History size={24} className="text-slate-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Logs de Auditoría</h1>
            <p className="text-slate-600">Historial de acciones y cambios en el sistema</p>
          </div>
        </div>
        <Button onClick={loadLogs} variant="outline" disabled={loading} data-testid="refresh-audit-btn">
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border">
          <TabsTrigger value="general" className="data-[state=active]:bg-slate-100" data-testid="tab-general">
            <Activity size={16} className="mr-2" />
            Auditoría General
          </TabsTrigger>
          <TabsTrigger value="cierre" className="data-[state=active]:bg-teal-100" data-testid="tab-cierre">
            <Database size={16} className="mr-2" />
            Cierre de Período / F8125
          </TabsTrigger>
        </TabsList>

        {/* Tab: General */}
        <TabsContent value="general" className="space-y-4">
          <Card className="p-4 bg-slate-50 border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <Filter size={16} className="text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filtros de servidor</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Usuario</Label>
                <Input
                  placeholder="Buscar por usuario..."
                  value={filters.usuario}
                  data-testid="filter-usuario-input"
                  onChange={(e) => setFilters({ ...filters, usuario: e.target.value })}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Entidad</Label>
                <Select
                  value={filters.entidad || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, entidad: v === 'all' ? '' : v })}
                >
                  <SelectTrigger className="h-9" data-testid="filter-entidad-select">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="monitor">Monitor</SelectItem>
                    <SelectItem value="alert">Alerta</SelectItem>
                    <SelectItem value="caso">Caso</SelectItem>
                    <SelectItem value="usuario">Usuario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Acción</Label>
                <Input
                  placeholder="Buscar por acción..."
                  value={filters.accion}
                  data-testid="filter-accion-input"
                  onChange={(e) => setFilters({ ...filters, accion: e.target.value })}
                  className="h-9"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={loadLogs} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="search-audit-btn">
                  <Search size={14} className="mr-1" />
                  Buscar
                </Button>
                <Button onClick={clearFilters} size="sm" variant="outline" data-testid="clear-filters-btn">
                  Limpiar
                </Button>
              </div>
            </div>
          </Card>

          <Card className="bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-medium text-slate-900" data-testid="general-log-count">
                {processedGeneral.length} registros encontrados
              </h3>
            </div>
            <DataTable
              columns={generalColumnsWithActions}
              data={processedGeneral}
              loading={loading}
              emptyMessage="No se encontraron registros"
              emptyIcon={<History size={32} />}
              sortConfig={sortConfig}
              onSort={handleSort}
              columnFilters={columnFilters}
              onColumnFilterChange={handleColumnFilterChange}
              onRowClick={(row) => { setSelectedLog(row); setShowDetailDialog(true); }}
            />
          </Card>
        </TabsContent>

        {/* Tab: Cierre de Período */}
        <TabsContent value="cierre" className="space-y-4">
          <Card className="p-4 bg-teal-50 border-teal-200">
            <div className="flex items-center gap-2 mb-3">
              <Filter size={16} className="text-teal-600" />
              <span className="text-sm font-medium text-teal-700">Filtros</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-teal-600">Período (YYYYMM)</Label>
                <Input
                  placeholder="Ej: 202602"
                  value={filters.periodo}
                  data-testid="filter-periodo-input"
                  onChange={(e) => setFilters({ ...filters, periodo: e.target.value })}
                  className="h-9"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={loadLogs} size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="search-cierre-btn">
                  <Search size={14} className="mr-1" />
                  Buscar
                </Button>
                <Button onClick={clearFilters} size="sm" variant="outline">
                  Limpiar
                </Button>
              </div>
            </div>
          </Card>

          <Card className="bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-medium text-slate-900" data-testid="cierre-log-count">
                {processedCierre.length} registros de cierre y F8125
              </h3>
            </div>
            <DataTable
              columns={cierreColumnsWithActions}
              data={processedCierre}
              loading={loading}
              emptyMessage="No se encontraron registros"
              emptyIcon={<Database size={32} />}
              sortConfig={sortConfig}
              onSort={handleSort}
              columnFilters={columnFilters}
              onColumnFilterChange={handleColumnFilterChange}
              onRowClick={(row) => { setSelectedLog(row); setShowDetailDialog(true); }}
            />
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog de Detalle */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={18} className="text-slate-600" />
              Detalle del Registro
            </DialogTitle>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4 py-4" data-testid="audit-log-detail">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Fecha/Hora</Label>
                  <p className="font-medium">
                    {formatDate(selectedLog.timestamp || selectedLog.fecha)}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Usuario</Label>
                  <p className="font-medium">
                    {selectedLog.usuario_nombre || selectedLog.usuario || selectedLog.usuario_id || '-'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Acción</Label>
                  <Badge className={getActionBadgeColor(selectedLog.accion)}>
                    {selectedLog.accion}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Entidad/Tipo</Label>
                  <p className="font-medium capitalize">
                    {selectedLog.entidad || selectedLog.origen || '-'}
                    {selectedLog.tipo && ` - ${selectedLog.tipo}`}
                  </p>
                </div>
              </div>

              {(selectedLog.entidad_id || selectedLog.reporte_id) && (
                <div>
                  <Label className="text-xs text-slate-500">ID de Entidad</Label>
                  <p className="font-mono text-sm bg-slate-100 p-2 rounded">
                    {selectedLog.entidad_id || selectedLog.reporte_id}
                  </p>
                </div>
              )}

              {selectedLog.periodo && (
                <div>
                  <Label className="text-xs text-slate-500">Período</Label>
                  <p className="font-medium">{formatPeriodo(selectedLog.periodo)}</p>
                </div>
              )}

              {(selectedLog.detalles || selectedLog.comentarios) && (
                <div>
                  <Label className="text-xs text-slate-500">Detalles/Comentarios</Label>
                  <div className="bg-slate-50 p-3 rounded-lg text-sm mt-1">
                    {typeof selectedLog.detalles === 'object' ? (
                      <pre className="text-xs overflow-auto">
                        {JSON.stringify(selectedLog.detalles, null, 2)}
                      </pre>
                    ) : (
                      <p>{selectedLog.detalles || selectedLog.comentarios || '-'}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogs;
